import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { SITE_URL } from "@/hooks/use-seo";
import {
  CLINIC_REF,
  DEFAULT_OG_IMAGE,
  buildBreadcrumbList,
  Crumb,
} from "@/components/schemaConstants";
import Breadcrumbs from "@/components/Breadcrumbs";
import { RelatedLink, RelatedPages } from "./RelatedPages";
import ClinicalReviewNote from "./ClinicalReviewNote";
import { ArticleInlineCta } from "./ArticleInlineCta";
import EligibilityCheckForm from "./EligibilityCheckForm";
import { SaintEligibilityCheck } from "./SaintEligibilityCheck";
import {
  FAQAccordionItem,
  SectionRenderer,
  ContentSection,
} from "./ContentSections";

export interface ContentSeo {
  title: string;
  description: string;
  canonical: string;
  keywords?: string;
  ogImage?: string;
  jsonLd?: object | object[];
  /** Visible breadcrumb trail (e.g. [{name:"SAINT®", href:"/saint"}]).
   *  When provided, renders visible breadcrumbs + BreadcrumbList schema. */
  breadcrumbs?: Crumb[];
  /** Extra schema blocks to append (e.g. VideoObject, MedicalProcedure). */
  extraSchema?: object | object[];
  /** When true, adds a noindex,nofollow robots meta tag. */
  noIndex?: boolean;
}

/* ── Content block types (re-exported for consumers) ── */
export interface FAQItem {
  question: string;
  answer: string;
}
export interface ContentCard {
  title: string;
  body: string;
  icon?: string;
}
export interface ComparisonRow {
  feature: string;
  columns: string[];
}
export type { ContentSection };

export interface DepressionContentPageProps {
  seo: ContentSeo;
  badge?: string;
  heroTitle: string;
  heroSubtitle?: string;
  heroCtaPrimary?: string;
  heroCtaPrimaryHref?: string;
  heroCtaSecondary?: string;
  heroImage?: string;
  sections: ContentSection[];
  faq?: FAQItem[];
  faqHeading?: string;
  relatedLinks?: RelatedLink[];
  relatedHeading?: string;
  finalCtaHeading?: string;
  finalCtaText?: string;
  finalCtaPrimaryLabel?: string;
  finalCtaPrimaryHref?: string;
  finalCtaSecondaryLabel?: string;
  finalCtaSecondaryHref?: string;
  /** E-E-A-T: named medical reviewer (e.g. "Daniel Pietras, MD"). */
  reviewedBy?: string;
  /** E-E-A-T: ISO 8601 date of last clinical review. */
  lastUpdated?: string;
  /** Extra disclaimer text rendered above the footer (e.g. competitor notice). */
  footerDisclaimer?: string;
  /** When true, renders the EligibilityCheckForm beside the hero headline
   *  in a conversion-first split layout (like /saint-google). */
  heroForm?: boolean;
  /** Source label for the hero eligibility form. */
  heroFormSource?: string;
  /** When true, renders the 3-question SaintEligibilityCheck (Google Ads
   *  variant) in the hero form slot instead of the 7-field EligibilityCheckForm. */
  heroSaintForm?: boolean;
  /** Heading for the saint hero eligibility form. */
  heroFormHeading?: string;
}

export const DepressionContentPage: React.FC<DepressionContentPageProps> = ({
  seo,
  badge,
  heroTitle,
  heroSubtitle,
  heroCtaPrimary = "Schedule a Private Consultation",
  heroCtaPrimaryHref = "/#consultation",
  heroCtaSecondary = "Call (954) 827-9793",
  heroImage,
  sections,
  faq,
  faqHeading = "Frequently Asked Questions",
  relatedLinks,
  relatedHeading = "Explore Related Resources",
  finalCtaHeading = "You Don't Have to Figure This Out Alone",
  finalCtaText = "Talk with our team about your treatment history, your questions, and the options that may be worth discussing. No commitment — just a conversation.",
  finalCtaPrimaryLabel = "Schedule a Private Consultation",
  finalCtaPrimaryHref = "/#consultation",
  finalCtaSecondaryLabel,
  finalCtaSecondaryHref = "tel:9548279793",
  reviewedBy,
  lastUpdated,
  footerDisclaimer,
  heroForm = false,
  heroFormSource = "saint-content-page",
  heroSaintForm = false,
  heroFormHeading,
}) => {
  const canonicalPath = seo.canonical.replace(/\/+$/, "") || "/";
  const url = `${SITE_URL.replace(/\/$/, "")}${canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`}`;
  const headline = seo.title.split("|")[0].trim();
  const jsonLd: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline,
      description: seo.description,
      url,
      image: seo.ogImage || DEFAULT_OG_IMAGE,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": url,
      },
      author: {
        "@type": "Organization",
        name: "Neuro Wellness TMS Centers of America",
        url: SITE_URL,
      },
      publisher: CLINIC_REF,
      reviewedBy: {
        "@id": "https://neurowellnesstms.com/dr-daniel-pietras-md#physician",
      },
      ...(lastUpdated
        ? { datePublished: lastUpdated, dateModified: lastUpdated }
        : {}),
    },
  ];
  if (seo.breadcrumbs && seo.breadcrumbs.length > 0) {
    jsonLd.push(buildBreadcrumbList(seo.breadcrumbs, canonicalPath));
  }
  if (faq && faq.length > 0) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.answer,
        },
      })),
    });
  }
  if (seo.extraSchema) {
    const extra = Array.isArray(seo.extraSchema)
      ? seo.extraSchema
      : [seo.extraSchema];
    jsonLd.push(...extra);
  }
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        ogImage={seo.ogImage}
        keywords={seo.keywords}
        noIndex={seo.noIndex}
        schemaJson={jsonLd}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* HERO — clean navy background */}
        <section className="relative text-primary-foreground py-16 sm:py-28 overflow-hidden bg-primary">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-primary-deep/90" />
          <div
            className={
              heroForm
                ? "container-px relative mx-auto max-w-6xl px-4 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center"
                : "container-px relative mx-auto max-w-5xl px-4"
            }
          >
            <div className="max-w-xl text-left space-y-5 order-2 lg:order-1">
              <div className="flex flex-wrap items-center gap-3">
                {badge && (
                  <span className="inline-block rounded-full bg-accent/90 backdrop-blur-sm px-4 py-1.5 text-xs sm:text-sm font-bold text-white shadow-lg uppercase tracking-wider">
                    {badge}
                  </span>
                )}
                <div className="inline-flex items-center rounded-xl bg-white px-2.5 py-1 shadow-sm border border-border">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/861c17b1-d72f-46dc-9f82-4d1ea308284e.png"
                    alt="SAINT by Magnus Medical"
                    className="h-6 sm:h-7 w-auto object-contain"
                    width={140}
                    height={35}
                    loading="lazy"
                  />
                </div>
              </div>
              <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight drop-shadow-md">
                {heroTitle}
              </h1>
              {heroSubtitle && (
                <p className="text-base sm:text-lg text-primary-foreground/90 leading-relaxed drop-shadow-md">
                  {heroSubtitle}
                </p>
              )}
              {!heroForm && !heroSaintForm && (
                <div className="flex flex-col sm:flex-row items-start justify-start gap-4 pt-3">
                  <Button
                    asChild
                    size="lg"
                    className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90 text-base px-8 py-6"
                  >
                    <a href={heroCtaPrimaryHref}>{heroCtaPrimary}</a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-white/60 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:border-white/80 text-base px-8 py-6"
                  >
                    <a href="tel:9548279793">
                      <Phone className="h-5 w-5 mr-2" />
                      Call 954-827-9793
                    </a>
                  </Button>
                </div>
              )}
            </div>
            {(heroForm || heroSaintForm) && (
              <div className="w-full order-1 lg:order-2">
                {heroSaintForm ? (
                  <div id="eligibility">
                    <p className="mb-3 text-xs font-bold uppercase tracking-wider text-accent">
                      Free insurance benefits check
                    </p>
                    <SaintEligibilityCheck
                      source={heroFormSource}
                      variant="card"
                      googleAds
                      redirectTo="/thank-you-saint-google"
                      heading={
                        heroFormHeading ?? "Who Qualifies for SAINT® TMS?"
                      }
                    />
                  </div>
                ) : (
                  <EligibilityCheckForm
                    source={heroFormSource}
                    redirectTo="/thank-you-saint-google"
                  />
                )}
              </div>
            )}
          </div>
        </section>

        {/* Breadcrumbs */}
        {seo.breadcrumbs && seo.breadcrumbs.length > 0 && (
          <div className="bg-background pt-4">
            <div className="container-px mx-auto max-w-4xl px-4">
              <Breadcrumbs crumbs={seo.breadcrumbs} />
            </div>
          </div>
        )}

        {/* E-E-A-T: medical review / last-updated note */}
        {(reviewedBy || lastUpdated) && (
          <div className="bg-background pt-6">
            <div className="container-px mx-auto max-w-4xl px-4">
              <ClinicalReviewNote
                reviewedBy={reviewedBy}
                lastUpdated={lastUpdated}
              />
            </div>
          </div>
        )}

        {/* TOP inline CTA — appears right after the intro so readers can act early */}
        <ArticleInlineCta variant="top" />

        {/* CONTENT SECTIONS — mid CTA injected after the halfway point */}
        {sections.map((section, idx) => (
          <React.Fragment key={idx}>
            <SectionRenderer section={section} idx={idx} />
            {sections.length > 3 &&
              idx === Math.floor(sections.length / 2) - 1 && (
                <ArticleInlineCta variant="mid" />
              )}
          </React.Fragment>
        ))}

        {/* FAQ */}
        {faq && faq.length > 0 && (
          <section className="bg-background py-12 sm:py-16">
            <div className="container-px mx-auto max-w-3xl px-4 space-y-4">
              <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-2">
                {faqHeading}
              </h2>
              <div className="space-y-3">
                {faq.map((item, idx) => (
                  <FAQAccordionItem key={idx} item={item} idx={idx} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* RELATED PAGES */}
        {relatedLinks && relatedLinks.length > 0 && (
          <RelatedPages
            heading={relatedHeading}
            links={relatedLinks}
            className="bg-light-blue/30"
          />
        )}

        {/* END inline CTA — final push before the gradient CTA banner */}
        <ArticleInlineCta variant="end" />

        {/* FINAL CTA */}
        <section className="bg-gradient-navy py-14 sm:py-20">
          <div className="container-px mx-auto max-w-2xl px-4 text-center space-y-5">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-white">
              {finalCtaHeading}
            </h2>
            <p className="text-primary-foreground/80 text-base sm:text-lg leading-relaxed">
              {finalCtaText}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href={finalCtaPrimaryHref}>{finalCtaPrimaryLabel}</a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70"
              >
                <a href={finalCtaSecondaryHref}>
                  {finalCtaSecondaryHref === "tel:9548279793" && (
                    <Phone className="h-4 w-4 mr-2" />
                  )}
                  {finalCtaSecondaryLabel || "(954) 827-9793"}
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      {footerDisclaimer && (
        <div className="bg-background border-t border-border">
          <div className="container-px mx-auto max-w-4xl px-4 py-8">
            <p className="text-xs text-muted-foreground leading-relaxed">
              {footerDisclaimer}
            </p>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};
export default DepressionContentPage;
